import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Power, Send, MessageSquarePlus, Copy, Star, RefreshCw, Code2,
  ShieldX, TrendingUp, Users, Save, Bell, ExternalLink, CheckCircle2,
  QrCode, Printer, Mail, Megaphone, Globe, AlertTriangle, PhoneCall, Eye, EyeOff, ShieldCheck,
} from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { UpgradeCard } from "@/components/UpgradeCard";
import { HygieneNote } from "@/components/HygieneNote";
import { ReadinessCard } from "@/components/ReadinessCard";

const STATUS_STYLE = {
  sent: "bg-primary/10 text-primary border-primary/30",
  clicked: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
  reviewed: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  queued: "bg-muted text-muted-foreground border-border",
};

function Section({ title, icon: Icon, children, action }) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {Icon && <Icon className="h-4 w-4" />} {title}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function ReputationTab({ site }) {
  const siteId = site.id;
  const [rep, setRep] = useState(null);
  const [smsOk, setSmsOk] = useState(false);
  const [embedBase, setEmbedBase] = useState(null);   // public host from the server, never built here
  const snippet = (widget, attr, div) => (embedBase
    ? `<script async src="${embedBase}/api/embed/${widget}.js" ${attr}="${rep?.form?.token}"></script>\n<div id="${div}"></div>`
    : "Embed host not set on the server. Set REPUTATION_PUBLIC_BASE (or APP_ORIGIN), then reload.");
  const [busy, setBusy] = useState("");
  const [dash, setDash] = useState(null);
  const [requests, setRequests] = useState([]);
  const [leads, setLeads] = useState([]);
  const [reviews, setReviews] = useState(null);
  const [reviewMeta, setReviewMeta] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [hyg, setHyg] = useState({});           // WO9: per-draft cleaning note, flags, facts
  const [embed, setEmbed] = useState(null);
  const [lastLink, setLastLink] = useState(null);
  const [gate, setGate] = useState(null);     // set when this site lacks the Reviews tier

  // form fields
  const [form, setForm] = useState({ place_id: "", business_name: "", plan: "reviews", message_template: "", chips: "" });
  const [contact, setContact] = useState({ name: "", phone: "" });
  const [textAdd, setTextAdd] = useState("");
  const [optOut, setOptOut] = useState("");
  const [wsc, setWsc] = useState({ title: "", min_stars: 4, per_page: 8, powered_by: true, accent_override: "" });
  const [wrv, setWrv] = useState({ heading: "", subtext: "", powered_by: true, accent_override: "" });
  const [domain, setDomain] = useState("");
  const cnameTarget = (() => { try { return new URL(process.env.REACT_APP_BACKEND_URL).host; } catch { return ""; } })();

  const loadAll = async () => {
    const { data } = await api.get(`/sites/${siteId}/reputation`);
    setRep(data.reputation);
    setSmsOk(data.sms_configured);
    setEmbedBase(data.embed_base || null);
    setForm({
      place_id: data.reputation.place_id || "",
      business_name: data.reputation.business_name || "",
      plan: data.reputation.plan || "reviews",
      message_template: data.reputation.message_template || "",
      chips: (data.reputation.chips || []).join(", "),
    });
    const sc = data.reputation.showcase || {};
    setWsc({ title: sc.title || "Trusted by People Like You", min_stars: sc.min_stars ?? 4, per_page: sc.per_page ?? 8, powered_by: sc.powered_by ?? true, accent_override: sc.accent_override || "" });
    const rw = data.reputation.review_widget || {};
    setWrv({ heading: rw.heading || "How did we do?", subtext: rw.subtext || "", powered_by: rw.powered_by ?? true, accent_override: rw.accent_override || "" });
    setDomain((data.reputation.custom_domain || {}).host || "");
    if (data.reputation.enabled) {
      const [d, r, l, f] = await Promise.all([
        api.get(`/sites/${siteId}/reputation/dashboard`),
        api.get(`/sites/${siteId}/reputation/requests`),
        api.get(`/sites/${siteId}/reputation/leads`),
        api.get(`/sites/${siteId}/reputation/feedback`),
      ]);
      setDash(d.data);
      setRequests(r.data.requests);
      setLeads(l.data.leads);
      setFeedback(f.data);
    }
  };

  useEffect(() => {
    loadAll().catch((err) => {
      const d = err.response?.data?.detail;
      if (err.response?.status === 403 && d?.code === "upgrade_required") setGate(d);
    });
  }, [siteId]);

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  const enable = () => act("enable", async () => { await api.post(`/sites/${siteId}/reputation/enable`); await loadAll(); toast.success("Reputation enabled"); });
  const disable = () => act("disable", async () => { await api.post(`/sites/${siteId}/reputation/disable`); await loadAll(); toast.success("Reputation disabled"); });

  const saveSettings = () => act("save", async () => {
    await api.patch(`/sites/${siteId}/reputation`, {
      place_id: form.place_id || null,
      business_name: form.business_name || null,
      message_template: form.message_template,
      chips: form.chips.split(",").map((c) => c.trim()).filter(Boolean),
    });
    await loadAll();
    toast.success("Settings saved");
  });

  const createRequest = () => act("req", async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/request`, contact);
    setLastLink({ ...data, name: contact.name });
    setContact({ name: "", phone: "" });
    await loadAll();
    toast.success("Review request ready — tap to send");
  });

  const textToAdd = () => act("textadd", async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/text-to-add`, { text: textAdd });
    setLastLink({ ...data, name: data.request.name });
    setTextAdd("");
    await loadAll();
    toast.success("Parsed and queued");
  });

  const remind = (id) => act("rem" + id, async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/requests/${id}/remind`);
    if (data.sms_link) { setLastLink({ sms_link: data.sms_link, message: data.message }); }
    await loadAll();
    toast.success("Reminder ready");
  });

  const addOptOut = () => act("opt", async () => {
    await api.post(`/sites/${siteId}/reputation/opt-out`, { phone: optOut });
    setOptOut("");
    toast.success("Added to suppression list");
  });

  const markLead = (id, status) => act("lead" + id, async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/leads/${id}/status`, { status });
    if (data.review_request) { setLastLink(data.review_request); toast.success("Won — review request fired"); }
    else if (data.review_request_skipped) toast.message("Status updated", { description: data.review_request_skipped });
    else toast.success("Lead updated");
    await loadAll();
  });

  const loadReviews = () => act("reviews", async () => {
    const { data } = await api.get(`/sites/${siteId}/reputation/reviews`);
    setReviews(data.reviews);
    setReviewMeta({ hidden_count: data.hidden_count, gate_pending: data.gate_pending });
  });
  const setVisibility = (rev, action) => act("v" + rev.id, async () => {
    await api.post(`/sites/${siteId}/reputation/reviews/${rev.id}/visibility`, { action });
    const { data } = await api.get(`/sites/${siteId}/reputation/reviews`);
    setReviews(data.reviews);
    setReviewMeta({ hidden_count: data.hidden_count, gate_pending: data.gate_pending });
    toast.success(action === "show" ? "Review un-hidden — it will show in the widget" : action === "hide" ? "Review hidden from the widget" : "Back to the automatic decision");
  });
  const runGate = () => act("gate", async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/reviews/moderate`);
    toast.success(`Checked ${data.checked} review(s) · ${data.hidden} hidden` + (data.unavailable ? ` · ${data.unavailable} could not be checked` : ""));
    const { data: rv } = await api.get(`/sites/${siteId}/reputation/reviews`);
    setReviews(rv.reviews);
    setReviewMeta({ hidden_count: rv.hidden_count, gate_pending: rv.gate_pending });
  });
  const loadFeedback = () => act("feedback", async () => {
    const { data } = await api.get(`/sites/${siteId}/reputation/feedback`);
    setFeedback(data);
  });
  const markHandled = (f) => act("h" + f.id, async () => {
    await api.post(`/sites/${siteId}/reputation/feedback/${f.id}/handled`, { handled: !f.handled });
    const { data } = await api.get(`/sites/${siteId}/reputation/feedback`);
    setFeedback(data);
  });
  const draftReply = (rev) => act("d" + rev.id, async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/reviews/${rev.id}/reply-draft`, { rating: rev.rating, text: rev.text });
    setDrafts((p) => ({ ...p, [rev.id]: data.draft }));
    setHyg((p) => ({ ...p, [rev.id]: { ...(data.hygiene || {}), facts_changed: data.facts_changed || [], original: data.draft } }));
  });
  const postReply = (rev) => act("p" + rev.id, async () => { await api.post(`/sites/${siteId}/reputation/reviews/${rev.id}/reply`, { comment: drafts[rev.id] }); toast.success("Reply posted"); });
  const blockedReply = (rev) => {
    const h = hyg[rev.id];
    return !!h?.facts_changed?.length && drafts[rev.id] === h.original;
  };

  const loadEmbed = () => act("embed", async () => { const { data } = await api.get(`/sites/${siteId}/reputation/embed`); setEmbed(data); });
  const copy = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
      toast.success("Copied");
    } catch {
      toast.message("Press Ctrl+C to copy", { description: t });
    }
  };

  const saveDomain = () => act("domain", async () => {
    await api.post(`/sites/${siteId}/reputation/domain`, { host: domain });
    await loadAll();
    toast.success("Domain saved — add the DNS records below, then verify");
  });
  const verifyDomain = () => act("verifydomain", async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/domain/verify`);
    if (data.verified) toast.success("Domain verified — review links now use your domain");
    else toast.error(`Not verified yet (CNAME: ${data.checks.cname ? "ok" : "missing"}, TXT: ${data.checks.txt ? "ok" : "missing"}). DNS can take time to propagate.`);
    await loadAll();
  });
  const removeDomain = () => act("rmdomain", async () => {
    await api.delete(`/sites/${siteId}/reputation/domain`);
    setDomain("");
    await loadAll();
    toast.success("Custom domain removed");
  });

  const saveWidgets = () => act("widgets", async () => {
    await api.patch(`/sites/${siteId}/reputation`, {
      showcase: { title: wsc.title, min_stars: Number(wsc.min_stars), per_page: Number(wsc.per_page), powered_by: wsc.powered_by, accent_override: wsc.accent_override || null },
      review_widget: { heading: wrv.heading, subtext: wrv.subtext, powered_by: wrv.powered_by, accent_override: wrv.accent_override || null },
    });
    await loadAll();
    toast.success("Widget settings saved");
  });

  const sendResults = () => act("results", async () => {
    const { data } = await api.post(`/sites/${siteId}/reputation/send-results`, {});
    if (data.sent) toast.success(`Results email sent to ${data.to}`);
    else if (!data.to) toast.message("No recipient configured", { description: "Add a report recipient for this site to send the monthly results email." });
    else toast.message("Email is in mock mode", { description: `Configure SMTP to deliver. Recipient: ${data.to}` });
  });

  if (gate) {
    return (
      <div className="mx-auto max-w-lg" data-testid="reputation-upgrade">
        <UpgradeCard siteId={siteId} offer={gate.upgrade} />
      </div>
    );
  }

  if (!rep) return <div className="flex justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  if (!rep.enabled) {
    return (
      <div className="mx-auto max-w-lg rounded-lg border border-dashed border-border p-8 text-center" data-testid="reputation-disabled">
        <Star className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
        <h3 className="font-display text-xl font-semibold">Reputation engine</h3>
        <p className="mt-2 text-sm text-muted-foreground">Make leaving a Google review effortless — tap-to-send requests, a branded review page, a lead-capture form, and owner reply tools. Every request always includes the Google link (never gated).</p>
        {!smsOk && <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">Automated sending (Reviews + Social plan and up) activates once an SMS provider is configured on the server. Tap-to-send works today.</p>}
        <Button className="mt-5" onClick={enable} disabled={busy === "enable"} data-testid="reputation-enable">
          {busy === "enable" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Power className="mr-2 h-4 w-4" />} Enable reputation
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reputation-tab">
      <ReadinessCard siteId={siteId} />

      {/* Dashboard */}
      {dash && (
        <Section title="Dashboard" icon={TrendingUp} action={
          <Button size="sm" variant="outline" onClick={sendResults} disabled={busy === "results"} data-testid="rep-send-results">
            {busy === "results" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1.5 h-3.5 w-3.5" />} Email results
          </Button>
        }>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label="Review velocity" value={`${dash.velocity.per_week}/wk`} sub={`${dash.velocity.per_month}/mo`} />
            <Stat label="Requests sent" value={dash.requests_sent} />
            <Stat label="Click rate" value={`${Math.round(dash.click_rate * 100)}%`} sub={`${dash.clicks} clicks`} />
            <Stat label="Reviews (est.)" value={dash.reviews_attributed_est} sub={`${Math.round(dash.conversion_rate * 100)}% conv.`} />
            <Stat label="90-day rating" value={dash.rating.last_90_days ?? "—"} sub={`lifetime ${dash.rating.lifetime ?? "—"}`} />
            <Stat label="Leads / mo" value={dash.leads.per_month} sub={`${Math.round(dash.leads.lead_to_review_rate * 100)}% → review`} />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{dash.attribution_note}</p>
        </Section>
      )}

      {/* Tap-to-send */}
      <Section title="Send a review request (tap-to-send)" icon={Send}>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-muted-foreground">Customer name</label>
            <Input value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} placeholder="Mike Jones" data-testid="rep-contact-name" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-xs text-muted-foreground">Mobile number</label>
            <Input value={contact.phone} onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))} placeholder="813-555-1234" data-testid="rep-contact-phone" />
          </div>
          <Button onClick={createRequest} disabled={busy === "req" || !contact.phone} data-testid="rep-create-request">
            {busy === "req" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Create
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Input value={textAdd} onChange={(e) => setTextAdd(e.target.value)} placeholder='Text-to-add: "Mike Jones 813-555-1234"' data-testid="rep-text-add-input" />
          <Button variant="outline" onClick={textToAdd} disabled={busy === "textadd" || !textAdd} data-testid="rep-text-add"><MessageSquarePlus className="mr-2 h-4 w-4" /> Add</Button>
        </div>
        {lastLink && (
          <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="rep-last-link">
            <p className="text-sm font-medium">Request ready for {lastLink.name || "customer"}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {lastLink.sms_link && <a href={lastLink.sms_link} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground" data-testid="rep-tap-send"><Send className="h-3.5 w-3.5" /> Tap to send SMS</a>}
              {lastLink.landing_url && <Button size="sm" variant="ghost" onClick={() => copy(lastLink.landing_url)}><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy link</Button>}
              {lastLink.landing_url && <a href={lastLink.landing_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /> Preview page</a>}
            </div>
          </div>
        )}
      </Section>

      {/* Requests */}
      <Section title={`Requests (${requests.length})`} icon={Users}>
        {requests.length === 0 ? <p className="text-sm text-muted-foreground">No requests yet.</p> : (
          <div className="divide-y divide-border">
            {requests.slice(0, 20).map((rq) => (
              <div key={rq.id} className="flex items-center justify-between gap-2 py-2.5" data-testid="rep-request-row">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{rq.name || rq.phone}</p>
                  <p className="font-mono text-xs text-muted-foreground">{rq.phone} · {rq.channel}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={STATUS_STYLE[rq.status]}>{rq.status}</Badge>
                  {!rq.reviewed_at && (rq.reminder_count || 0) < 1 && (
                    <Button size="sm" variant="ghost" onClick={() => remind(rq.id)} disabled={busy === "rem" + rq.id} data-testid="rep-remind">
                      {busy === "rem" + rq.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Leads */}
      <Section title={`Leads (${leads.length})`} icon={Bell}>
        {leads.length === 0 ? <p className="text-sm text-muted-foreground">No leads yet. Embed the form on the client's site below.</p> : (
          <div className="divide-y divide-border">
            {leads.slice(0, 15).map((l) => (
              <div key={l.id} className="flex items-center justify-between gap-2 py-2.5" data-testid="rep-lead-row">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{l.name} · <span className="font-mono text-xs text-muted-foreground">{l.phone}</span></p>
                  <p className="truncate text-xs text-muted-foreground">{l.service || "—"} · {l.message || ""}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline">{l.status}</Badge>
                  {l.status === "new" && (
                    <Button size="sm" variant="outline" onClick={() => markLead(l.id, "won")} disabled={busy === "lead" + l.id} data-testid="rep-lead-won">
                      {busy === "lead" + l.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />} Won
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Private feedback — owner-only triage */}
      <Section title={`Private feedback${feedback?.needs_callback ? ` — ${feedback.needs_callback} need a call today` : ""}`} icon={AlertTriangle}
        action={<Button size="sm" variant="ghost" onClick={loadFeedback} disabled={busy === "feedback"} data-testid="rep-feedback-refresh"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh</Button>}>
        {!feedback || feedback.feedback.length === 0 ? (
          <p className="text-sm text-muted-foreground">No private feedback yet. This channel never reaches Google — it comes straight to the owner.</p>
        ) : (
          <div className="space-y-2">
            {feedback.untagged > 0 && (
              <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="rep-feedback-untagged">
                {feedback.untagged} item(s) saved but not yet sorted — sorting retries automatically. Nothing is ever lost.
              </p>
            )}
            {feedback.feedback.slice(0, 20).map((f) => {
              const t = f.triage || {};
              const urgent = t.needs_callback && !f.handled;
              return (
                <div key={f.id} className={`rounded-md border p-3 ${urgent ? "border-red-500/40 bg-red-500/5" : "border-border bg-background"}`} data-testid="rep-feedback-row">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {urgent && <Badge className="border-red-500/30 bg-red-500/15 text-red-600 dark:text-red-400" data-testid="rep-feedback-urgent"><PhoneCall className="mr-1 h-3 w-3" /> Call today</Badge>}
                    <Badge variant="outline">{t.category ? t.category.replace(/_/g, " ") : "not sorted yet"}</Badge>
                    {t.severity_label && <Badge variant="outline">{t.severity_label} · {t.severity}/5</Badge>}
                    {f.handled && <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400">handled</Badge>}
                    <span className="ml-auto truncate text-xs text-muted-foreground">{f.name || "Anonymous"}{f.phone ? ` · ${f.phone}` : ""}</span>
                  </div>
                  <p className="mt-2 text-sm">{f.message}</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => markHandled(f)} disabled={busy === "h" + f.id} data-testid="rep-feedback-handled">
                    {busy === "h" + f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />} {f.handled ? "Reopen" : "Mark handled"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Google reviews / owner reply */}
      <Section title="Reviews — showcase gate &amp; owner reply" icon={Star} action={
        <div className="flex gap-1.5">
          <Button size="sm" variant="ghost" onClick={runGate} disabled={busy === "gate"} data-testid="rep-run-gate">
            {busy === "gate" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="mr-1.5 h-3.5 w-3.5" />} Run gate
          </Button>
          <Button size="sm" variant="ghost" onClick={loadReviews} disabled={busy === "reviews"}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Load</Button>
        </div>}>
        {reviews === null ? <p className="text-sm text-muted-foreground">Load reviews from the connected Google Business profile (via the Social add-on).</p> :
          reviews.length === 0 ? <p className="text-sm text-muted-foreground">No reviews in the feed yet.</p> : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground" data-testid="rep-gate-summary">
                {reviewMeta.hidden_count || 0} hidden from the widget for content reasons{reviewMeta.gate_pending ? ` · ${reviewMeta.gate_pending} not checked yet (still showing)` : ""}. Star rating is never a reason to hide, and review text is never changed. Your headline score and count always stay the real Google/Facebook totals.
              </p>
              {reviews.map((rev) => (
                <div key={rev.id} className={`rounded-md border p-3 ${rev.hidden ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-background"}`} data-testid="rep-review-row">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="flex items-center gap-1 text-amber-500">{Array.from({ length: rev.rating || 0 }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}</span>
                    {!rev.rating && rev.recommendation === "recommended" && <Badge variant="outline">recommends</Badge>}
                    {rev.hidden && <Badge className="border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400" data-testid="rep-review-hidden">Hidden from widget</Badge>}
                    {rev.gate_pending && <Badge variant="outline">not checked yet</Badge>}
                    {rev.visibility_override && <Badge variant="outline">manual: {rev.visibility_override}</Badge>}
                  </div>
                  {rev.hidden_reasons?.length > 0 && (
                    <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400" data-testid="rep-review-hidden-reason">Why: {rev.hidden_reasons.join(" · ")}</p>
                  )}
                  <p className="mt-1 text-sm">{rev.text || <span className="text-muted-foreground">(no text)</span>}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {rev.hidden ? (
                      <Button size="sm" variant="outline" onClick={() => setVisibility(rev, "show")} disabled={busy === "v" + rev.id} data-testid="rep-review-unhide">
                        {busy === "v" + rev.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="mr-1 h-3.5 w-3.5" />} Un-hide
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setVisibility(rev, "hide")} disabled={busy === "v" + rev.id} data-testid="rep-review-hide">
                        {busy === "v" + rev.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EyeOff className="mr-1 h-3.5 w-3.5" />} Hide
                      </Button>
                    )}
                    {rev.visibility_override && (
                      <Button size="sm" variant="ghost" onClick={() => setVisibility(rev, "auto")} disabled={busy === "v" + rev.id} data-testid="rep-review-auto">Reset to automatic</Button>
                    )}
                  </div>
                  {drafts[rev.id] !== undefined ? (
                    <div className="mt-2 space-y-2">
                      <Textarea value={drafts[rev.id]} onChange={(e) => setDrafts((d) => ({ ...d, [rev.id]: e.target.value }))} className="text-sm" rows={3} />
                      <HygieneNote info={hyg[rev.id]} factsChanged={hyg[rev.id]?.facts_changed} testId={`rep-hygiene-${rev.id}`} />
                      <Button size="sm" onClick={() => postReply(rev)} disabled={busy === "p" + rev.id || blockedReply(rev)} data-testid="rep-post-reply">Post reply</Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => draftReply(rev)} disabled={busy === "d" + rev.id} data-testid="rep-draft-reply">Draft AI reply</Button>
                  )}
                </div>
              ))}
            </div>
          )}
      </Section>

      {/* Lead form embed */}
      <Section title="Lead capture form — embed" icon={Code2} action={<Button size="sm" variant="ghost" onClick={loadEmbed} disabled={busy === "embed"}>Get snippet</Button>}>
        {embed ? (
          <div className="space-y-3">
            <div>
              <div className="mb-1.5 flex items-center justify-between"><span className="text-xs text-muted-foreground">Paste before &lt;/body&gt; on any page</span><Button size="sm" variant="ghost" onClick={() => copy(embed.script)} data-testid="rep-copy-embed"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button></div>
              <pre className="overflow-auto rounded-md border border-border bg-background p-3 font-mono text-xs">{embed.script}</pre>
            </div>
            <p className="text-xs text-muted-foreground">SMS terms &amp; privacy page: <a href={embed.terms_url} target="_blank" rel="noreferrer" className="text-primary underline">{embed.terms_url}</a></p>
          </div>
        ) : <p className="text-sm text-muted-foreground">Generate a copy-paste embed snippet that works on any website, styled from the client's brand.</p>}
      </Section>

      {/* On-site kit: QR + printable card */}
      {rep.form?.token && (
        <Section title="On-site kit — QR &amp; printable card" icon={QrCode}>
          <div className="flex flex-wrap items-center gap-5">
            <img
              src={`${API}/review/${rep.form.token}/qr.png`}
              alt="Review QR code"
              className="h-32 w-32 rounded-lg border border-border bg-white p-2"
              data-testid="rep-qr-image"
            />
            <div className="flex-1 min-w-[220px] space-y-2">
              <p className="text-sm text-muted-foreground">Crews can show this QR or hand customers a printed card. It opens the client-branded review page — no app, no typing.</p>
              <div className="flex flex-wrap gap-2">
                <a href={`${API}/review/${rep.form.token}/card.pdf`} target="_blank" rel="noreferrer" data-testid="rep-download-card"
                  className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:bg-accent">
                  <Printer className="h-3.5 w-3.5" /> Download printable card
                </a>
                <Button size="sm" variant="ghost" onClick={() => copy(embedBase ? `${embedBase}/review/${rep.form.token}` : "Embed host not set on the server. Set REPUTATION_PUBLIC_BASE (or APP_ORIGIN), then reload.")} data-testid="rep-copy-review-url">
                  <Copy className="mr-1.5 h-3.5 w-3.5" /> Copy review link
                </Button>
                <a href={`${embedBase || process.env.REACT_APP_BACKEND_URL}/review/${rep.form.token}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                  <ExternalLink className="h-3.5 w-3.5" /> Preview
                </a>
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Review widgets (showcase + leave-a-review) */}
      {rep.form?.token && (
        <Section title="Review widgets" icon={Megaphone} action={
          <Button size="sm" onClick={saveWidgets} disabled={busy === "widgets"} data-testid="rep-save-widgets">
            {busy === "widgets" ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Save widget settings
          </Button>
        }>
          <p className="mb-4 text-sm text-muted-foreground">Two embeddable widgets that match the client's live reviews page. The headline score always reflects the real Google totals; "Write a review" uses the no-gating review page.</p>

          {/* Widget 1: showcase */}
          <div className="rounded-md border border-border bg-background p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold">1 · Showcase widget</span>
              <a href={`${API}/embed/preview/${rep.form.token}?widget=showcase`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="rep-preview-showcase"><ExternalLink className="h-3.5 w-3.5" /> Live preview</a>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">Title</label>
                <Input value={wsc.title} onChange={(e) => setWsc((w) => ({ ...w, title: e.target.value }))} data-testid="rep-sc-title" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Accent override (hex, optional)</label>
                <Input value={wsc.accent_override} onChange={(e) => setWsc((w) => ({ ...w, accent_override: e.target.value }))} placeholder="#3ab795" data-testid="rep-sc-accent" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Minimum stars to show</label>
                <select value={wsc.min_stars} onChange={(e) => setWsc((w) => ({ ...w, min_stars: e.target.value }))} data-testid="rep-sc-minstars" className="mt-0.5 flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value={1}>1 star and up</option>
                  <option value={2}>2 stars and up</option>
                  <option value={3}>3 stars and up</option>
                  <option value={4}>4 stars and up</option>
                  <option value={5}>5 stars only</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Cards per page</label>
                <Input type="number" value={wsc.per_page} onChange={(e) => setWsc((w) => ({ ...w, per_page: e.target.value }))} data-testid="rep-sc-perpage" />
              </div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={wsc.powered_by} onChange={(e) => setWsc((w) => ({ ...w, powered_by: e.target.checked }))} data-testid="rep-sc-poweredby" /> Show "Powered by LocalPro"
            </label>
            <div className="mt-3 mb-1.5 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Paste before &lt;/body&gt; on the client's page</span>
              <Button size="sm" variant="ghost" onClick={() => copy(snippet("showcase", "data-lp-showcase", "lp-reviews"))} data-testid="rep-copy-showcase"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
            </div>
            <pre className="overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs" data-testid="rep-showcase-snippet">{snippet("showcase", "data-lp-showcase", "lp-reviews")}</pre>
          </div>

          {/* Widget 2: leave a review */}
          <div className="mt-4 rounded-md border border-border bg-background p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold">2 · "Leave us a review" widget</span>
              <a href={`${API}/embed/preview/${rep.form.token}?widget=review`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline" data-testid="rep-preview-review"><ExternalLink className="h-3.5 w-3.5" /> Live preview</a>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-muted-foreground">Heading</label>
                <Input value={wrv.heading} onChange={(e) => setWrv((w) => ({ ...w, heading: e.target.value }))} data-testid="rep-rw-heading" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Accent override (hex, optional)</label>
                <Input value={wrv.accent_override} onChange={(e) => setWrv((w) => ({ ...w, accent_override: e.target.value }))} placeholder="#3ab795" data-testid="rep-rw-accent" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground">Subtext</label>
                <Textarea value={wrv.subtext} onChange={(e) => setWrv((w) => ({ ...w, subtext: e.target.value }))} rows={2} className="text-sm" data-testid="rep-rw-subtext" />
              </div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={wrv.powered_by} onChange={(e) => setWrv((w) => ({ ...w, powered_by: e.target.checked }))} data-testid="rep-rw-poweredby" /> Show "Powered by LocalPro"
            </label>
            <p className="mt-2 text-xs text-muted-foreground">Every visitor sees the Google button and the private-feedback link on first render — no star-gating.</p>
            <div className="mt-3 mb-1.5 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Paste before &lt;/body&gt; on the client's page</span>
              <Button size="sm" variant="ghost" onClick={() => copy(snippet("review", "data-lp-review", "lp-review"))} data-testid="rep-copy-review-widget"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
            </div>
            <pre className="overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs" data-testid="rep-review-widget-snippet">{snippet("review", "data-lp-review", "lp-review")}</pre>
          </div>
        </Section>
      )}

      {/* Custom review domain (CNAME) */}
      {rep.form?.token && (
        <Section title="Custom review domain" icon={Globe}>
          <p className="mb-3 text-sm text-muted-foreground">Serve review links, the QR page, and both widgets from the client's own domain (e.g. <span className="font-mono">reviews.theirbrand.com</span>). Add the DNS records below, then verify.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground">Subdomain</label>
              <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="reviews.yourbrand.com" data-testid="rep-domain-input" />
            </div>
            <Button onClick={saveDomain} disabled={busy === "domain" || !domain} data-testid="rep-domain-save">
              {busy === "domain" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
            </Button>
            {rep.custom_domain?.host && (
              <Button variant="outline" onClick={verifyDomain} disabled={busy === "verifydomain"} data-testid="rep-domain-verify">
                {busy === "verifydomain" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Verify
              </Button>
            )}
            {rep.custom_domain?.host && (
              <Button variant="ghost" onClick={removeDomain} disabled={busy === "rmdomain"} data-testid="rep-domain-remove">Remove</Button>
            )}
          </div>
          {rep.custom_domain?.host && (
            <div className="mt-3 flex items-center gap-2" data-testid="rep-domain-status">
              <Badge variant="outline" className={rep.custom_domain.status === "verified" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" : "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"}>
                {rep.custom_domain.status === "verified" ? "Verified · live" : "Pending verification"}
              </Badge>
              <span className="font-mono text-sm">{rep.custom_domain.host}</span>
            </div>
          )}
          {rep.custom_domain?.host && rep.custom_domain.status !== "verified" && (
            <div className="mt-3 rounded-md border border-border bg-muted/30 p-3" data-testid="rep-domain-dns">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">DNS records to add</p>
              <div className="space-y-1.5 font-mono text-xs">
                <div><span className="inline-block w-12 text-muted-foreground">CNAME</span> {rep.custom_domain.host} <span className="text-muted-foreground">→</span> {cnameTarget}</div>
                <div><span className="inline-block w-12 text-muted-foreground">TXT</span> _localpro-verify.{rep.custom_domain.host} <span className="text-muted-foreground">→</span> {rep.custom_domain.verify_token}</div>
              </div>
            </div>
          )}
        </Section>
      )}

      {/* Settings */}
      <Section title="Settings" icon={Save}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs text-muted-foreground">Business name (white-label)</label>
            <Input value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))} data-testid="rep-business-name" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Google Place ID {rep.place_id ? "" : "(auto-fills from connected GBP)"}</label>
            <Input value={form.place_id} onChange={(e) => setForm((f) => ({ ...f, place_id: e.target.value }))} placeholder="ChIJ…" data-testid="rep-place-id" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Plan</label>
            <p className="mt-1.5 text-sm" data-testid="rep-plan">
              {{ reviews: "Reviews — $29/mo", reviews_social: "Reviews + Social — $59/mo", complete: "Complete — $99/mo" }[form.plan] || form.plan}
              <span className="block text-xs text-muted-foreground">
                Set by the add-on, not here. Owners and admins change it on the Add-ons page.
              </span>
            </p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Review chips (comma-separated)</label>
            <Input value={form.chips} onChange={(e) => setForm((f) => ({ ...f, chips: e.target.value }))} data-testid="rep-chips" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs text-muted-foreground">Message template — use {"{first_name}"}, {"{business}"}, {"{link}"} (incentives blocked)</label>
            <Textarea value={form.message_template} onChange={(e) => setForm((f) => ({ ...f, message_template: e.target.value }))} rows={3} className="text-sm" data-testid="rep-template" />
          </div>
        </div>
        <Button className="mt-3" onClick={saveSettings} disabled={busy === "save"} data-testid="rep-save">
          {busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save settings
        </Button>
      </Section>

      {/* Suppression */}
      <Section title="Opt-out / suppression" icon={ShieldX}>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">Add a number to the do-not-contact list</label>
            <Input value={optOut} onChange={(e) => setOptOut(e.target.value)} placeholder="813-555-0000" data-testid="rep-optout-input" />
          </div>
          <Button variant="outline" onClick={addOptOut} disabled={busy === "opt" || !optOut} data-testid="rep-optout-add"><ShieldX className="mr-2 h-4 w-4" /> Suppress</Button>
        </div>
      </Section>

      <div className="flex justify-end">
        <Button variant="ghost" size="sm" onClick={disable} disabled={busy === "disable"} data-testid="reputation-disable"><Power className="mr-2 h-4 w-4" /> Disable reputation</Button>
      </div>
    </div>
  );
}
